<template>
  <section class="saved-ranges-settings settings">
    <header>
      <h1 class="page-header">Saved ranges</h1>
    </header>
    <p v-if="noSavedRanges">No saved ranges.</p>
    <div class="row">
      <div class="col-sm-6">
        <section class="saved-ranges">
          <saved-range
            v-for="(savedRange, index) in savedRanges"
            v-bind:key="savedRange.id"
            v-bind:savedRange="savedRange"
            v-bind:indexes="[index]"
            v-bind:savedRanges="savedRanges"
            v-bind:indexesOfSelectedSavedRange="indexesOfSelectedSavedRange"
            v-on:showRange="showRange($event)"
            v-on:remove="remove($event)"
            v-on:addChild="addChild($event)"
            v-on:addSibling="addSibling($event)"
            v-on:moveUp="moveUp($event)"
            v-on:moveDown="moveDown($event)"
            v-on:indentRight="indentRight($event)"
            v-on:indentLeft="indentLeft($event)"
          />
        </section>
      </div>
      <div class="col-sm-6">
        <section v-if="selectedSavedRange" class="selected-saved-range">
          <h3>{{ selectedRangeName }}</h3>
          <grid-selector
            v-if="!shortDeck"
            v-bind:generalHands="selectedSavedRange.generalHands"
            v-bind:specificHandsNotAllowed="true"
            v-on:selectGeneralHand="selectGeneralHand($event.generalHandString, selectedSavedRange.generalHands)"
            v-on:deselectGeneralHand="deselectGeneralHand($event, selectedSavedRange.generalHands)"
            v-on:selectGeneralHands="selectGeneralHands($event, selectedSavedRange.generalHands)"
            v-on:deselectGeneralHands="deselectGeneralHands($event, selectedSavedRange.generalHands)"
          />
          <short-deck-grid-selector
            v-if="shortDeck"
            v-bind:generalHands="selectedSavedRange.generalHands"
            v-bind:specificHandsNotAllowed="true"
            v-on:selectGeneralHand="selectGeneralHand($event.generalHandString, selectedSavedRange.generalHands)"
            v-on:deselectGeneralHand="deselectGeneralHand($event, selectedSavedRange.generalHands)"
            v-on:selectGeneralHands="selectGeneralHands($event, selectedSavedRange.generalHands)"
            v-on:deselectGeneralHands="deselectGeneralHands($event, selectedSavedRange.generalHands)"
          />
          <slider-selector
            ref="sliderSelector"
            v-bind:percentOfHandsInRangeNumberForDisplay="percentOfHandsInRangeNumberForDisplay"
            v-bind:numberOfSpecificHandsInRange="numberOfSpecificHandsInSelectedSavedRange"
            v-bind:numberOfPossibleSpecificHands="numberOfPossibleSpecificHands"
            v-bind:disabled="false"
            v-on:setRangeToMatchDoubleSlider="setRangeToMatchDoubleSlider($event)"
          />
          <text-selector
            v-bind:generalHands="selectedSavedRange.generalHands"
            v-on:setRangeToMatchSpecificHandStrings="setRangeToMatchSpecificHandStrings($event)"
          />
        </section>
      </div>
    </div>
    <hr>
    <section class="buttons">
      <button class="btn btn-default btn-lg" v-on:click="isHelpVisible = !isHelpVisible">
        <span v-if="!isHelpVisible">Show help <span class="caret-down"></span></span>
        <span v-if="isHelpVisible">Hide help <span class="caret-up"></span></span>
      </button>
      <button class="btn btn-primary btn-lg" v-on:click="save()">Save</button>
    </section>
    <section v-if="isHelpVisible" class="well">
      <section class="embed-responsive embed-responsive-16by9">
        <iframe class="embed-responsive-item" width="560" height="315" src="https://www.youtube.com/embed/mbm1unyFNMQ?rel=0&amp;showinfo=0" frameborder="0" allow="autoplay; encrypted-media" allowfullscreen></iframe>
      </section>
      <hr>
      <p>Note: The default ranges come from <a href="https://redchippoker.com/infographic-pre-flop-ranges/">Ed Miller's recommendations</a>. If you want to create your own ranges but need help, I'd highly recommend reading <a href="https://edmillerpoker.com/product/how-to-read-hands-at-no-limit-holdem/">How To Read Hands At No-Limit Hold’em</a>. Or for a quicker read, check out <a href="https://splitsuit.com/range-building-open-raises-preflop">Range Building - What Did They Open-Raise Preflop</a>.</p>
      <h3>1. Set a range</h3>
      <img src="/static/settings/saved-ranges-1.png">
      <h3>2. Click save</h3>
      <img src="/static/settings/saved-ranges-2.png">
      <h3>3. Click to add the range</h3>
      <img src="/static/settings/saved-ranges-3.png">
      <h3>4. The range that you set in step 1 will be added</h3>
      <img src="/static/settings/saved-ranges-4.png">
    </section>
  </section>
</template>

<script>
import SavedRange from '@/components/settings/general/saved-range.vue';
import GridSelector from '@/components/hand-range/grid-selector/grid-selector';
import ShortDeckGridSelector from '@/components/short-deck/short-deck-grid-selector';
import SliderSelector from '@/components/hand-range/slider-selector/slider-selector';
import TextSelector from '@/components/hand-range/text-selector/text-selector';
import utilities from '@/services/utilities/utilities';
import GeneralHands from '@/services/hands/GeneralHands';
import constants from '@/services/constants';
import _ from 'lodash';
import settingsValidators from '@/services/settings-validators';

export default {
  name: 'saved-ranges-settings',
  components: {
    'saved-range': SavedRange,
    'grid-selector': GridSelector,
    'short-deck-grid-selector': ShortDeckGridSelector,
    'slider-selector': SliderSelector,
    'text-selector': TextSelector,
  },
  data: function () {
    return {
      savedRanges: null,
      selectedSavedRange: null,
      indexesOfSelectedSavedRange: null,
      isHelpVisible: false,
      percentOfHandsInRangeNumberForDisplay: 0,
    };
  },
  computed: {
    shortDeck: function () {
      return this.$store.getters['account/purchasedShortDeck'];
    },
    numberOfPossibleSpecificHands: function () {
      if (this.shortDeck) {
        return 630;
      }

      return 1326;
    },
    noSavedRanges: function () {
      return this.savedRanges && this.savedRanges.length === 0;
    },
    selectedRangeName: function () {
      if (!this.indexesOfSelectedSavedRange) {
        return '';
      }

      let name = '';
      let currArray = this.savedRanges;

      this.indexesOfSelectedSavedRange.forEach(function (index) {
        name += currArray[index].name + ' / ';
        currArray = currArray[index].children;
      });

      return name.slice(0, -2);
    },
    includedSelectors: function () {
      return this.$store.state.account.user.settings.general.includedSelectors;
    },
    numberOfSpecificHandsInSelectedSavedRange: function () {
      let numberOfSpecificHandsInSelectedSavedRange = 0;

      this.selectedSavedRange.generalHands.forEachSpecificHandInRange(function (specificHand) {
        numberOfSpecificHandsInSelectedSavedRange += specificHand.weight / 100;
      });

      return numberOfSpecificHandsInSelectedSavedRange;
    },
  },
  mounted: function () {
    this.initialize();
  },
  methods: {
    // ranges
    remove: function (indexes) {
      let arr = utilities.general.getArrayFromNextToLastArrayIndex(this.savedRanges, indexes);
      arr.splice(indexes[indexes.length - 1], 1);

      if (_.isEqual(indexes, this.indexesOfSelectedSavedRange)) {
        this.showFirstRange();
      }

      // delete parents if they have no children anymore
      if (arr.length === 0) {
        indexes.pop();
        this.remove(indexes);
      }
    },
    showRange: function (indexes) {
      let el = utilities.general.getArrayItemFromArrayOfIndexes(this.savedRanges, indexes);
      this.selectedSavedRange = el;
      this.indexesOfSelectedSavedRange = indexes;
      this.updatePercentOfHandsInRangeDisplay();
    },
    showFirstRange: function () {
      let indexesArrayOfRangeToShow = [];
      let currArray = this.savedRanges;

      while (true) {
        indexesArrayOfRangeToShow.push(0);
        if (currArray[0].children) {
          currArray = currArray[0].children;
        } else {
          break;
        }
      }

      this.showRange(indexesArrayOfRangeToShow);
    },
    addChild: function (indexes) {
      let el = utilities.general.getArrayItemFromArrayOfIndexes(this.savedRanges, indexes);
      let generalHands;

      generalHands = new GeneralHands();

      if (this.shortDeck) {
        generalHands.adjustForShortDeck();
      }

      if (!el.children) {
        this.$set(el, 'collapsed', false);
        this.$set(el, 'children', []);
      }

      el.children.push({
        name: 'New range',
        editNameMode: false,
        generalHands: generalHands,
        numberOfPossibleSpecificHands: this.numberOfPossibleSpecificHands,
      });
    },
    addSibling: function (indexes) {
      let arr = utilities.general.getArrayFromNextToLastArrayIndex(this.savedRanges, indexes);
      let generalHands;

      generalHands = new GeneralHands();

      if (this.shortDeck) {
        generalHands.adjustForShortDeck();
      }

      arr.splice(indexes[indexes.length - 1] + 1, 0, {
        name: 'New range',
        editNameMode: false,
        generalHands: generalHands,
        numberOfPossibleSpecificHands: this.numberOfPossibleSpecificHands,
      });
    },
    moveUp: function (indexes) {
      let el = utilities.general.getArrayItemFromArrayOfIndexes(this.savedRanges, indexes);
      let arr = utilities.general.getArrayFromNextToLastArrayIndex(this.savedRanges, indexes);
      arr.splice(indexes[indexes.length - 1], 1);

      // space to move up on the current level
      if (indexes[indexes.length - 1] > 0) {
        arr.splice(indexes[indexes.length - 1] - 1, 0, el);
      }

      // first element of the current level
      else {
        indexes.pop();
        arr = utilities.general.getArrayFromNextToLastArrayIndex(this.savedRanges, indexes);
        arr.splice(indexes[indexes.length - 1], 0, el);
      }
    },
    moveDown: function (indexes) {
      let el = utilities.general.getArrayItemFromArrayOfIndexes(this.savedRanges, indexes);
      let arr = utilities.general.getArrayFromNextToLastArrayIndex(this.savedRanges, indexes);

      // space to move down the current level
      if (arr[indexes[indexes.length - 1] + 1]) {
        arr.splice(indexes[indexes.length - 1], 1);
        arr.splice(indexes[indexes.length - 1] + 1, 0, el);
      }

      // no space to move down the current level
      else {
        throw new Error('moveDown shouldn\'t be possible.');
      }
    },
    indentRight: function (indexes) {
      let arr;
      let el;
      let indexesForElementAbove;
      let elAbove;

      arr = utilities.general.getArrayFromNextToLastArrayIndex(this.savedRanges, indexes);
      el = utilities.general.getArrayItemFromArrayOfIndexes(this.savedRanges, indexes);
      indexesForElementAbove = _.cloneDeep(indexes);
      indexesForElementAbove[indexesForElementAbove.length - 1]--;
      elAbove = utilities.general.getArrayItemFromArrayOfIndexes(this.savedRanges, indexesForElementAbove);

      if (!elAbove.children) {
        this.$set(elAbove, 'collapsed', false);
        this.$set(elAbove, 'children', []);
      }

      elAbove.children.push(el);
      arr.splice(indexes[indexes.length - 1], 1);
    },
    indentLeft: function (indexes) {
      let el = utilities.general.getArrayItemFromArrayOfIndexes(this.savedRanges, indexes);
      let arr = utilities.general.getArrayFromNextToLastArrayIndex(this.savedRanges, indexes);
      let parentArr;

      arr.splice(indexes[indexes.length - 1], 1);
      indexes.pop();
      parentArr = utilities.general.getArrayFromNextToLastArrayIndex(this.savedRanges, indexes);
      parentArr.splice(indexes[indexes.length - 1] + 1, 0, el);

      if (arr.length === 0) {
        parentArr.splice(indexes[indexes.length - 1], 1);
      }
    },
    save: function () {
      let self = this;
      let user = _.cloneDeep(this.$store.state.account.user);

      utilities.general.forEachLeaf(this.savedRanges, function (leaf) {
        leaf.handsInRange = [];
        leaf.generalHands.forEach(function (generalHand) {
          if (generalHand.inRange === true) {
            leaf.handsInRange.push(generalHand.string);
          }
        });
      });

      if (!settingsValidators.areSavedRangesValid(this.savedRanges)) {
        alert('Make sure that each saved range has a name and that you didn\'t leave the range empty.');
        return;
      }

      user.settings.general.savedRanges = self._getSavedRanges();
      self.$http.put(`${process.env.BASE_API_URL}/user`, { user: user }).then(function (res) {
        self.$store.commit('account/setUser', res.data.user);
        alert('Saved!');
      }).catch(function () {
        alert('Unable to save settings.');
      });
    },
    _getSavedRanges: function () {
      let savedRanges = _.cloneDeep(this.savedRanges);

      utilities.general.forEachLeaf(savedRanges, function (leaf) {
        delete leaf.editNameMode;
        delete leaf.generalHands;
        delete leaf.numberOfPossibleSpecificHands;
      });
      utilities.general.forEachParent(savedRanges, function (parent) {
        delete parent.editNameMode;
        delete parent.collapsed;
        delete parent.numberOfPossibleSpecificHands;
        delete parent.generalHands;
        delete parent.handsInRange;
      });

      return savedRanges;
    },

    // range
    _selectGeneralHand: function (generalHandString) {
      let generalHand = this.selectedSavedRange.generalHands.find(generalHandString);

      generalHand.forEachPossibleSpecificHand(function (specificHand) {
        if (!specificHand.inRange) {
          specificHand.weight = 100;
        }
      });
    },
    _deselectGeneralHand: function (generalHandString) {
      let generalHand = this.selectedSavedRange.generalHands.find(generalHandString);

      generalHand.forEachSpecificHandInRange(function (specificHand) {
        specificHand.weight = 0;
      });
    },
    _selectGeneralHands: function (generalHandStrings) {
      generalHandStrings.forEach(function (generalHandString) {
        this._selectGeneralHand(generalHandString);
      }, this);
    },
    _deselectGeneralHands: function (generalHandStrings) {
      generalHandStrings.forEach(function (generalHandString) {
        this._deselectGeneralHand(generalHandString);
      }, this);
    },
    _selectSpecificHand: function (specificHandString) {
      let specificHand = this.selectedSavedRange.generalHands.find(specificHandString);

      if (!specificHand.inRange) {
        specificHand.weight = 100;
      }
    },
    _deselectSpecificHand: function (specificHandString) {
      let specificHand = this.selectedSavedRange.generalHands.find(specificHandString);

      if (specificHand.inRange) {
        specificHand.weight = 0;
      }
    },
    _selectSpecificHands: function (specificHandStrings) {
      specificHandStrings.forEach(function (specificHandString) {
        this._selectSpecificHand(specificHandString);
      }, this);
    },
    _setRangeToMatchSpecificHandStrings: function (specificHandStrings) {
      this._clearRange();
      this._selectSpecificHands(specificHandStrings);
    },
    _clearRange: function () {
      let allGeneralHandStrings = constants.handRankings['sklansky-chubukov'];

      this._deselectGeneralHands(allGeneralHandStrings);
    },
    selectGeneralHand: function (generalHandString) {
      this._selectGeneralHand(generalHandString);
      this.updatePercentOfHandsInRangeDisplay();
      this.resetDoubleSlider();
    },
    deselectGeneralHand: function (generalHandString) {
      this._deselectGeneralHand(generalHandString);
      this.updatePercentOfHandsInRangeDisplay();
      this.resetDoubleSlider();
    },
    selectGeneralHands: function (generalHandStrings) {
      this._selectGeneralHands(generalHandStrings);
      this.updatePercentOfHandsInRangeDisplay();
      this.resetDoubleSlider();
    },
    deselectGeneralHands: function (generalHandStrings) {
      this._deselectGeneralHands(generalHandStrings);
      this.updatePercentOfHandsInRangeDisplay();
      this.resetDoubleSlider();
    },
    setRangeToMatchSpecificHandStrings: function (specificHandStrings) {
      this._setRangeToMatchSpecificHandStrings(specificHandStrings);
      this.updatePercentOfHandsInRangeDisplay();
      this.resetDoubleSlider();
    },
    setRangeToMatchDoubleSlider: function (percentilesObj) {
      let generalHandStringsToAddFromDoubleSlider = utilities.hands.getGeneralHandStringsToAddFromDoubleSlider(
        percentilesObj.topPercentile,
        percentilesObj.bottomPercentile,
        this.selectedSavedRange.generalHands,
        this.$store.getters['account/handRankings'],
      );

      this._setRangeToMatchSpecificHandStrings(generalHandStringsToAddFromDoubleSlider);
      this.updatePercentOfHandsInRangeDisplay();
    },
    resetDoubleSlider: function () {
      this.$refs.sliderSelector.resetDoubleSlider();
    },
    updatePercentOfHandsInRangeDisplay: function () {
      if (this.numberOfSpecificHandsInSelectedSavedRange === 0) {
        this.percentOfHandsInRangeNumberForDisplay = 0;
        return;
      }

      let exactPercentOfHandsInRange = (this.numberOfSpecificHandsInSelectedSavedRange / this.numberOfPossibleSpecificHands) * 100;

      if (exactPercentOfHandsInRange < 0.5) {
        this.percentOfHandsInRangeNumberForDisplay = 1;
        return;
      } else {
        this.percentOfHandsInRangeNumberForDisplay = Math.round(exactPercentOfHandsInRange);
        return;
      }
    },

    // initialize
    initialize: function () {
      this._initializeSavedRanges();
      this.showFirstRange();
      this.updatePercentOfHandsInRangeDisplay();
    },
    _initializeSavedRanges: function () {
      let savedRanges = _.cloneDeep(this.$store.state.account.user.settings.general.savedRanges);

      this.__initializePropertiesOfSavedRanges(savedRanges);
      this.__initializeHandsInRangeForSavedRanges(savedRanges);
      this.savedRanges = savedRanges;
    },
    __initializePropertiesOfSavedRanges: function (savedRanges) {
      let self = this;
      let generalHands;

      utilities.general.forEachLeaf(savedRanges, function (leaf) {
        generalHands = new GeneralHands();

        if (self.shortDeck) {
          generalHands.adjustForShortDeck();
        }

        leaf.editNameMode = false;
        leaf.generalHands = generalHands,
        leaf.numberOfPossibleSpecificHands = self.numberOfPossibleSpecificHands;
        leaf.id = _.uniqueId();
      });
      utilities.general.forEachParent(savedRanges, function (parent) {
        parent.editNameMode = false;
        parent.collapsed = false;
        parent.id = _.uniqueId();
      });
    },
    __initializeHandsInRangeForSavedRanges: function (savedRanges) {
      let self = this;

      utilities.general.forEachLeaf(savedRanges, function (savedRange) {
        self.___initializeHandsInRangeForSavedRange(savedRange);
      });
    },
    ___initializeHandsInRangeForSavedRange: function (savedRange) {
      savedRange.generalHands.forEach(function (generalHand) {
        generalHand.forEachPossibleSpecificHand(function (specificHand) {
          if (savedRange.handsInRange.includes(generalHand.string) && !specificHand.inRange) {
            specificHand.weight = 100;
          } else if (!savedRange.handsInRange.includes(generalHand.string) && specificHand.inRange) {
            specificHand.weight = 0;
          }
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  @import 'src/assets/settings';

  @media (min-width: 768px) {
    .saved-ranges {
      margin-top: 20px;
    }
  }

  .selected-saved-range {
    width: 353px;
  }

  .single-slider-selector /deep/ .labels .one-hundred {
    top: 10.5px;
  }

  .text-selector /deep/ input.form-control, .text-selector /deep/ button.btn {
    font-size: 12px;
  }

  img {
    width: 400px;
    margin-bottom: 10px;
  }

  @media (max-width: 768px) {
    .selected-saved-range {
      width: 353px;
      max-width: 100%;
    }
  }

  @media (max-width: 375px) {
    .selected-saved-range {
      width: 316px;
    }
  }

  @media (max-width: 320px) {
    .selected-saved-range {
      width: 262px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/settings/general/saved-ranges.vue